import { createEffect, createMemo, createSignal, For, on, onCleanup, Show } from 'solid-js';
import type { SkullOwnerNbt } from '../models/itemStack';
import { resolveSkin } from '../services/skullTexture';
import { skullHeadRenderer } from '../renderer/SkullHeadRenderer';
import { showSkullOwner, SKULL_VIEWER_FLAG } from './SkullOwnerView';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { itemSpriteSrc, tintedItemLayers, type TintedLayer } from '../services/itemSpriteLayers';
import styles from './MinecraftItem.module.css';

interface MinecraftItemProps {
    itemId?: number;
    itemIdentifier: string;
    metadata?: number;
    title?: string;
    size?: number | string;
    bare?: boolean;
    enchanted?: boolean;
    renderAsHead?: boolean;
    skullOwner?: SkullOwnerNbt;
    potionColor?: string;
    spawnEggColors?: { base: string; spot: string };
}

// What the game passes for a layer it does not colour, and multiplying by it is the identity, so an
// untinted layer needs no second code path.
const UNTINTED = '#ffffff';

// A caller that sizes its own tile passes a length ("100%") so the sprite grows with it; a plain
// number stays the pixel size every other call site gives.
export const cssSize = (size: number | string | undefined): string | undefined =>
    size === undefined ? undefined : typeof size === 'number' ? `${size}px` : size;

export default function MinecraftItem(props: MinecraftItemProps) {
    const imgSrc = () => itemSpriteSrc(props.itemIdentifier, props.metadata ?? 0);

    const tintedLayers = createMemo<TintedLayer[] | null>(() =>
        tintedItemLayers(props.itemIdentifier, props.potionColor, props.spawnEggColors),
    );

    const skullViewerEnabled = useFeatureFlag(SKULL_VIEWER_FLAG);

    const isBlock = () => props.itemId != null && props.itemId <= 255;
    const isHead = () => !!props.renderAsHead && !!props.skullOwner;
    const isClickableHead = () => isHead() && skullViewerEnabled();

    const [headSrc, setHeadSrc] = createSignal<string | undefined>();

    // Tracked on the owner's identity rather than the NBT object so a re-parsed but unchanged skull
    // does not resolve the skin and re-render the head again.
    const skullIdentity = createMemo(() => {
        const owner = isHead() ? props.skullOwner : undefined;
        if (!owner) return null;
        return owner.textureValue ?? owner.id ?? owner.name ?? null;
    });

    createEffect(on(skullIdentity, (identity) => {
        const owner = identity ? props.skullOwner : undefined;
        if (!owner) {
            setHeadSrc(undefined);
            return;
        }
        let cancelled = false;
        onCleanup(() => { cancelled = true; });

        resolveSkin(owner)
            .then((skin) => {
                if (cancelled || !skin) return undefined;
                const cached = skullHeadRenderer.getCached(skin.textureHash);
                if (cached) {
                    setHeadSrc(cached);
                    return undefined;
                }
                return skullHeadRenderer.renderHead(skin.skinUrl, skin.textureHash).then((url) => {
                    if (!cancelled) setHeadSrc(url);
                });
            })
            .catch(() => { });
    }));

    const displaySrc = () => (isHead() ? (headSrc() ?? imgSrc()) : imgSrc());

    // A damaged tool's imgSrc carries the damage value (e.g. "..._47.png"), which has no sprite of its
    // own: the <img> below falls back to "..._0.png" on error, but the CSS mask variable has no such
    // fallback, so it has to track whatever src the <img> actually ended up showing.
    const [resolvedSrc, setResolvedSrc] = createSignal(displaySrc());
    createEffect(on(displaySrc, setResolvedSrc));

    const sizeStyle = () => {
        const base: Record<string, string> = {};
        const size = cssSize(props.size);
        if (size) {
            base.width = size;
            base.height = size;
        }
        if (props.enchanted) {
            base['--enchant-mask'] = `url(${resolvedSrc()})`;
        }
        return base;
    };

    return (
        <div
            class={`${props.bare ? styles.bare : styles.itemIcon} ${isBlock() ? styles.blockBright : ''} ${props.enchanted ? styles.enchanted : ''} ${isClickableHead() ? styles.clickable : ''} ${tintedLayers() ? styles.layered : ''}`}
            title={props.title}
            style={sizeStyle()}
            onClick={isClickableHead() ? () => showSkullOwner(props.skullOwner!) : undefined}
        >
            <For each={tintedLayers() ?? []}>
                {(layer) => (
                    <div
                        class={styles.layer}
                        style={{ '--layer-src': `url(${layer.src})`, '--layer-tint': layer.tint ?? UNTINTED }}
                    />
                )}
            </For>
            {/* Kept behind the layers so the box keeps the size the sprite has always given it. */}
            <img
                class={tintedLayers() ? styles.sizer : undefined}
                src={displaySrc()}
                alt={props.itemIdentifier}
                decoding="async"
                // A sprite asked for before the resolver knew the item fails and hides; the real one may load later.
                onLoad={(e) => { (e.target as HTMLImageElement).style.display = ""; }}
                onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    const fallback = itemSpriteSrc(props.itemIdentifier, 0);
                    if (img.src !== fallback && !img.src.endsWith('_0.png') && !img.src.startsWith('data:')) {
                        img.src = fallback;
                        setResolvedSrc(fallback);
                    } else {
                        img.style.display = "none";
                    }
                }}
            />
            <Show when={props.enchanted}>
                <div class={styles.glint} />
            </Show>
        </div>
    );
}
